/**
 * MechanicLogsService - Web version
 * Manages mechanic interaction logs using localStorage
 */

export class MechanicLogsService {
  constructor() {
    this.logs = [];
    this.STORAGE_KEY = 'mechanic_logs';
    this.initialize();
  }

  static getInstance() {
    if (!MechanicLogsService.instance) {
      MechanicLogsService.instance = new MechanicLogsService();
    }
    return MechanicLogsService.instance;
  }

  /**
   * Initialize the service and load existing logs
   */
  initialize() {
    try {
      const storedLogs = localStorage.getItem(this.STORAGE_KEY);
      if (storedLogs) {
        this.logs = JSON.parse(storedLogs).map((log) => ({
          ...log,
          timestamp: new Date(log.timestamp)
        }));
      }
    } catch (error) {
      console.error('Error loading mechanic logs:', error);
      this.logs = [];
    }
  }

  /**
   * Add a new log entry
   */
  async addLog(logData) {
    try {
      const newLog = {
        ...logData,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        timestamp: new Date()
      };

      this.logs.unshift(newLog); // Add to beginning for newest first
      
      // Keep only last 1000 logs to prevent storage bloat
      if (this.logs.length > 1000) {
        this.logs = this.logs.slice(0, 1000);
      }

      await this.saveLogs();
      console.log('Mechanic log added:', newLog);
    } catch (error) {
      console.error('Error adding mechanic log:', error);
    }
  }

  /**
   * Get logs for a specific mechanic
   */
  getMechanicLogs(mechanicId) {
    return this.logs.filter(log => log.mechanicId === mechanicId);
  }

  /**
   * Get logs for a specific complaint
   */
  getComplaintLogs(complaintId) {
    return this.logs.filter(log => log.complaintId === complaintId);
  }

  /**
   * Get all logs
   */
  getAllLogs() {
    return [...this.logs];
  }

  /**
   * Get recent logs (last 50)
   */
  getRecentLogs() {
    return this.logs.slice(0, 50);
  }

  /**
   * Clear all logs
   */
  async clearLogs() {
    try {
      this.logs = [];
      localStorage.removeItem(this.STORAGE_KEY);
      console.log('All mechanic logs cleared');
    } catch (error) {
      console.error('Error clearing mechanic logs:', error);
    }
  }

  /**
   * Clear logs for a specific mechanic
   */
  async clearMechanicLogs(mechanicId) {
    try {
      this.logs = this.logs.filter(log => log.mechanicId !== mechanicId);
      await this.saveLogs();
      console.log(`Logs cleared for mechanic: ${mechanicId}`);
    } catch (error) {
      console.error('Error clearing mechanic logs:', error);
    }
  }

  /**
   * Save logs to storage
   */
  async saveLogs() {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.logs));
    } catch (error) {
      console.error('Error saving mechanic logs:', error);
    }
  }

  /**
   * Get action display text
   */
  getActionDisplayText(action) {
    const actionTexts = {
      copy_info: 'Copied mechanic information',
      call: 'Called mechanic',
      view_details: 'Viewed mechanic details',
      assign: 'Assigned mechanic',
      search: 'Searched for mechanics'
    };
    return actionTexts[action] || action;
  }

  /**
   * Get action icon name (for Lucide icons)
   */
  getActionIcon(action) {
    const actionIcons = {
      copy_info: 'Copy',
      call: 'Phone',
      view_details: 'Eye',
      assign: 'UserPlus',
      search: 'Search'
    };
    return actionIcons[action] || 'AlertCircle';
  }

  /**
   * Get action color
   */
  getActionColor(action) {
    const actionColors = {
      copy_info: '#10b981',
      call: '#3b82f6',
      view_details: '#6b7280',
      assign: '#8b5cf6',
      search: '#f59e0b'
    };
    return actionColors[action] || '#6b7280';
  }
}

export default MechanicLogsService;

